// Détection de langue Revyo — cascade front à 6 étages avec court-circuit.
// Ordre (le 1er match l'emporte) :
//   1. URL ?lang=xx                      → override absolu (testing/sharing)
//   2. localStorage.revyo_lang           → préférence utilisateur explicite
//   3. cookie revyo_lang                 → idem, mode cookie pour SSR-friendly
//   4. GET /api/i18n/detect (geoip)      → suggestion serveur, timeout 800ms
//   5. navigator.language                → fallback browser
//   6. DEFAULT_LANG ('fr')               → ultime fallback
//
// Toute valeur est normalisée (lowercase, base 'fr-FR' → 'fr', whitelist
// SUPPORTED_LANGS). Tout ce qui n'est pas dans la whitelist est ignoré.
import { SUPPORTED_LANGS, DEFAULT_LANG } from './config';

const STORAGE_KEY = 'revyo_lang';
const COOKIE_KEY = 'revyo_lang';
const DETECT_TIMEOUT_MS = 800;

function normalize(lang) {
  if (!lang || typeof lang !== 'string') return null;
  const base = lang.toLowerCase().split('-')[0];
  return SUPPORTED_LANGS.includes(base) ? base : null;
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  // Pas de regex dynamique sur input user — name est constant côté code.
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : null;
}

// Persistance du choix de langue (utilisée par le LanguageSwitcher).
// On valide STRICTEMENT contre la whitelist pour éviter toute pollution
// du localStorage / cookie par un attaquant via XSS chain ou via URL.
export function writeLanguageChoice(lang) {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) {}
  // SameSite=Lax + max-age 1 an. Pas de Secure ici car on supporte localhost en dev.
  // En prod (HTTPS), le navigateur ajoute automatiquement le contexte sécurisé.
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(lang)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

export async function detectInitialLanguage() {
  // 1. ?lang= dans l'URL — override absolu (et on le persiste pour la prochaine fois)
  try {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const fromUrl = normalize(url.searchParams.get('lang'));
      if (fromUrl) { writeLanguageChoice(fromUrl); return fromUrl; }
    }
  } catch (_) {}

  // 2. localStorage
  try {
    const stored = normalize(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch (_) {}

  // 3. cookie
  const fromCookie = normalize(readCookie(COOKIE_KEY));
  if (fromCookie) return fromCookie;

  // 4. backend geoip — timeout court pour ne pas retarder le premier paint
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DETECT_TIMEOUT_MS);
    const apiBase = process.env.REACT_APP_API_BASE || 'https://revyo-production.up.railway.app';
    const res = await fetch(`${apiBase}/api/i18n/detect`, {
      signal: controller.signal,
      credentials: 'omit',
    });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      const fromGeo = normalize(data && data.suggested_lang);
      if (fromGeo) return fromGeo;
    }
  } catch (_) {
    // Timeout ou réseau down → on continue vers les fallbacks
  }

  // 5. navigator.language
  try {
    if (typeof navigator !== 'undefined') {
      const fromNav = normalize(navigator.language || navigator.userLanguage);
      if (fromNav) return fromNav;
    }
  } catch (_) {}

  // 6. ultime fallback
  return DEFAULT_LANG;
}
